import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { M365User, M365Subscription } from "../types";
import { formatDistanceToNow } from "date-fns";
import { parseUtc } from "../utils/datetime";
import EntraUsers from "./EntraUsers";
import M365GroupsPanel from "../components/M365GroupsPanel";
import M365TeamsPanel from "../components/M365TeamsPanel";
import M365SharePointPanel from "../components/M365SharePointPanel";
import M365OneDrivePanel from "../components/M365OneDrivePanel";
import M365MailboxPanel from "../components/M365MailboxPanel";
import M365AutoConnectModal from "../components/M365AutoConnectModal";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { skuDisplay } from "../utils/m365";

type TenantLite = {
  id: number;
  customer_id: number;
  display_name: string | null;
  last_sync_at: string | null;
  last_sync_status: string | null;
};

export default function M365() {
  const [tab, setTab] = useState<
    "users" | "subscriptions" | "teams" | "groups" | "sharepoint" | "onedrive" | "mailboxes"
  >("users");
  const { currentCustomerId, current, customers } = useCustomer();
  const [users, setUsers] = useState<M365User[]>([]);
  const [subs, setSubs] = useState<M365Subscription[]>([]);
  const [tenants, setTenants] = useState<TenantLite[]>([]);
  const [showConfig, setShowConfig] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string>("");
  const subSort = useSort("sku");
  // In "All customers" the subscription rows are combined across every tenant,
  // and two "Microsoft 365 Business Premium" rows from different customers are
  // otherwise indistinguishable.
  const showCustomer = currentCustomerId == null;
  const customerName = (id?: number | null) =>
    (id != null ? customers.find((c) => c.id === id)?.name : null) || "—";

  const load = useCallback(() => {
    api.get("/api/v1/m365/tenants").then((r) => setTenants(r.data)).catch(() => setTenants([]));
    // Scope users/subscriptions to the selected customer (null = all tenants).
    const cfg = currentCustomerId != null ? { params: { customer_id: currentCustomerId } } : undefined;
    api.get("/api/v1/m365/users", cfg).then((r) => setUsers(r.data)).catch(() => setUsers([]));
    api
      .get("/api/v1/m365/subscriptions", cfg)
      .then((r) => setSubs(r.data))
      .catch(() => setSubs([]));
  }, [currentCustomerId]);
  useEffect(() => {
    load();
  }, [load]);

  // Per-customer connection state derived from the global switcher.
  const currentTenant =
    currentCustomerId != null
      ? tenants.find((t) => t.customer_id === currentCustomerId)
      : undefined;

  const triggerSync = async () => {
    setSyncing(true);
    setSyncResult("");
    try {
      // Sync just the selected customer's tenant, or all tenants in "All customers".
      const r = currentTenant
        ? await api.post(`/api/v1/m365/tenants/${currentTenant.id}/sync`)
        : await api.post("/api/v1/m365/sync");
      const d = r.data;
      if (d.ok) {
        setSyncResult(`Synced ${d.users ?? 0} users, ${d.subscriptions ?? 0} subscriptions`);
        load();
      } else {
        setSyncResult(`Sync failed: ${d.reason || ""}${d.detail ? " — " + d.detail : ""}`);
      }
    } catch (err: any) {
      setSyncResult(`Sync error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncResult(""), 8000);
    }
  };

  // parseUtc: backend timestamps are UTC but serialized without a zone, so
  // `new Date(...)` would read them as local time and drift by the UTC offset
  // (that's why it read "synced in about 4 hours").
  const syncedAt = parseUtc(users[0]?.last_sync_at || subs[0]?.last_sync_at);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Microsoft 365</h1>
          <p className="text-sm text-gray-500">
            {current
              ? `License + MFA inventory for ${current.name}'s tenant. Auto-syncs every 6 hours.`
              : "Per-customer M365 tenants — pick a customer in the header to connect or manage one. Auto-syncs every 6 hours."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {syncedAt && (
            <span className="text-xs text-gray-500">
              Synced {formatDistanceToNow(syncedAt, { addSuffix: true })}
            </span>
          )}
          <button
            onClick={triggerSync}
            disabled={syncing || (currentCustomerId != null && !currentTenant)}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {syncing ? "Syncing…" : currentTenant ? "Sync now" : "Sync all"}
          </button>
        </div>
      </div>

      {showConfig && currentCustomerId != null && (
        <M365AutoConnectModal
          customerId={currentCustomerId}
          customerName={current?.name}
          onClose={() => setShowConfig(false)}
          onDone={() => {
            load();
            triggerSync();
          }}
        />
      )}

      {syncResult && (
        <div
          className={`mb-4 text-sm px-4 py-2 rounded-lg ${
            syncResult.startsWith("Synced")
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {syncResult}
        </div>
      )}

      {/* A customer is selected but has no M365 tenant yet → connect prompt. */}
      {currentCustomerId != null && !currentTenant && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-yellow-900 mb-1">
              Connect Microsoft 365 for {current?.name}
            </h3>
            <p className="text-xs text-yellow-800">
              Connect this customer's Microsoft 365 tenant to enable license + MFA reporting and
              admin actions. One click: download the tool, run it once as a Global Admin, and it
              connects itself — no values to paste, no files to edit, no restart.
            </p>
          </div>
          <button
            onClick={() => setShowConfig(true)}
            className="shrink-0 text-sm bg-yellow-600 text-white px-4 py-1.5 rounded-lg hover:bg-yellow-700"
          >
            Connect Microsoft 365
          </button>
        </div>
      )}

      {/* "All customers" view → overview of connected tenants. */}
      {currentCustomerId == null && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-1">
            {tenants.length} customer tenant{tenants.length === 1 ? "" : "s"} connected
          </h3>
          <p className="text-xs text-blue-800">
            Pick a customer in the header switcher to connect a new tenant or manage an existing
            one (users, licenses, groups, SharePoint, mailboxes). The tables below show all tenants
            combined.
          </p>
        </div>
      )}

      <div className="flex gap-1 mb-4 border-b">
        {(
          ["users", "subscriptions", "teams", "groups", "sharepoint", "onedrive", "mailboxes"] as const
        ).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${
                tab === t
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t === "users"
                ? `Users (${users.length})`
                : t === "subscriptions"
                  ? `Subscriptions (${subs.length})`
                  : t === "teams"
                    ? "Teams"
                    : t === "groups"
                      ? "Entra Groups"
                      : t === "sharepoint"
                        ? "SharePoint"
                        : t === "onedrive"
                          ? "OneDrive"
                          : "Exchange"}
            </button>
          ),
        )}
      </div>

      {/* Full Entra user management (onboarding + lifecycle + licenses), embedded
          so the dashboard is the single hub — no separate "Entra Users" nav item. */}
      {tab === "users" && <EntraUsers embedded />}

      {tab === "subscriptions" && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
              <tr>
                <SortableTh label="SKU" sortKey="sku" sort={subSort.sort} onSort={subSort.toggle} />
                {showCustomer && (
                  <SortableTh label="Customer" sortKey="customer" sort={subSort.sort} onSort={subSort.toggle} />
                )}
                <SortableTh label="Part number" sortKey="part" sort={subSort.sort} onSort={subSort.toggle} />
                <SortableTh label="Total" sortKey="total" sort={subSort.sort} onSort={subSort.toggle} align="right" />
                <SortableTh label="Consumed" sortKey="consumed" sort={subSort.sort} onSort={subSort.toggle} align="right" />
                <SortableTh label="Available" sortKey="available" sort={subSort.sort} onSort={subSort.toggle} align="right" />
                <SortableTh label="Usage" sortKey="usage" sort={subSort.sort} onSort={subSort.toggle} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(subs, subSort.sort, (s, key) => {
                const avail = s.total_units - s.consumed_units;
                const usage = s.total_units === 0 ? 0 : s.consumed_units / s.total_units;
                switch (key) {
                  case "customer": return customerName(s.customer_id).toLowerCase();
                  case "part": return s.sku_part_number.toLowerCase();
                  case "total": return s.total_units;
                  case "consumed": return s.consumed_units;
                  case "available": return avail;
                  case "usage": return usage;
                  default: return skuDisplay(s.sku_part_number).toLowerCase();
                }
              }).map((s) => {
                const avail = s.total_units - s.consumed_units;
                const pct =
                  s.total_units === 0 ? 0 : Math.round((s.consumed_units / s.total_units) * 100);
                const pctCls =
                  pct >= 95 ? "text-red-700 dark:text-red-400" : pct >= 80 ? "text-orange-600 dark:text-orange-400" : "text-gray-700 dark:text-gray-300";
                return (
                  <tr
                    key={s.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    title={`${skuDisplay(s.sku_part_number)} (${s.sku_part_number})\n${s.consumed_units}/${s.total_units} used, ${avail} available`}
                  >
                    <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                      {skuDisplay(s.sku_part_number)}
                    </td>
                    {showCustomer && (
                      <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">
                        {customerName(s.customer_id)}
                      </td>
                    )}
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-500 dark:text-gray-400">
                      {s.sku_part_number}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">{s.total_units}</td>
                    <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">{s.consumed_units}</td>
                    <td className="px-4 py-2.5 text-right">
                      {avail > 0 ? (
                        <span className="text-gray-700 dark:text-gray-300">{avail}</span>
                      ) : avail === 0 ? (
                        <span className="text-orange-600 dark:text-orange-400">0</span>
                      ) : (
                        <span className="text-red-600 dark:text-red-400">{avail}</span>
                      )}
                    </td>
                    <td className={`px-4 py-2.5 text-right font-medium ${pctCls}`}>{pct}%</td>
                  </tr>
                );
              })}
              {subs.length === 0 && (
                <tr>
                  <td
                    colSpan={showCustomer ? 7 : 6}
                    className="px-4 py-10 text-center text-gray-400 dark:text-gray-500 text-sm"
                  >
                    No subscriptions yet. Click "Sync now" if configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "teams" && <M365TeamsPanel users={users} customerId={currentCustomerId} />}

      {tab === "groups" && <M365GroupsPanel users={users} customerId={currentCustomerId} />}

      {tab === "sharepoint" && <M365SharePointPanel customerId={currentCustomerId} />}

      {tab === "onedrive" && <M365OneDrivePanel users={users} customerId={currentCustomerId} />}

      {tab === "mailboxes" && <M365MailboxPanel users={users} customerId={currentCustomerId} />}
    </div>
  );
}
